<template>
  <v-container class="page-wrap">
    <v-row justify="center" no-gutters>
      <v-col cols="12">
        <!-- ACTIVE -->
        <div v-if="Business && Campaigns && Campaigns.isActive">
          <!-- Business Header -->
          <v-card class="card-soft mb-4" flat>
            <v-card-text class="pa-4">
              <div class="text-center">
                <v-avatar
                  v-if="Business.imageUrl"
                  size="72"
                  class="mx-auto mb-3"
                >
                  <img :src="Business.imageUrl" alt="Logo" />
                </v-avatar>

                <div v-if="Business.name" class="text-h6 font-weight-bold">
                  {{ Business.name }}
                </div>

                <div
                  v-if="Business.phoneNumber"
                  class="text-body-2 grey--text text--darken-1 mt-1"
                >
                  {{ Business.phoneNumber | USPhoneNumber }}
                </div>

                <div
                  v-if="hasAddress"
                  class="text-body-2 grey--text text--darken-2 mt-1"
                >
                  <span v-if="Business.address">{{ Business.address }}</span>
                  <span v-if="Business.city">, {{ Business.city }}</span>
                  <span v-if="Business.state">, {{ Business.state }}</span>
                  <span v-if="Business.zip"> {{ Business.zip }}</span>
                </div>
              </div>
            </v-card-text>
          </v-card>

          <!-- Campaign Title -->
          <div class="mb-3">
            <div class="text-h5 text-md-h4 font-weight-bold">
              {{ Campaigns.name }}
            </div>
            <div class="text-body-2 grey--text text--darken-1 mt-1">
              Created {{ Campaigns.createdAt | moment("from", "now") }}
            </div>
          </div>

          <!-- Campaign Body -->
          <v-card class="card-soft mb-4" outlined>
            <!-- Image (hide if not exists) -->
            <div v-if="Campaigns.imageUrl" class="hero-wrap">
              <v-img contain :src="Campaigns.imageUrl" class="hero-img" />
            </div>

            <v-card-text class="pa-4">
              <!-- Stats -->
              <div class="mb-2">
                <!-- NORMAL style (your existing Raised/Goal UI) -->
                <div v-if="!isInventoryCampaign">
                  <v-row dense>
                    <v-col cols="6" class="text-left">
                      <div class="stat-label">Raised</div>
                      <div class="stat-value">
                        {{ raisedAmount | currency }}
                      </div>
                    </v-col>

                    <v-col cols="6" class="text-right">
                      <div class="stat-label">Goal</div>
                      <div class="stat-value">{{ goalAmount | currency }}</div>
                    </v-col>

                    <v-col cols="12" class="pt-1">
                      <v-progress-linear
                        :value="progressPercent"
                        height="10"
                        rounded
                      />

                      <!-- META LINE -->
                      <v-row dense no-gutters class="mt-2 meta-row">
                        <v-col cols="4" class="text-left">
                          <span class="font-weight-bold">{{
                            Campaigns.donationCount || 0
                          }}</span>
                          <span class="grey--text text--darken-1">
                            donations</span
                          >
                        </v-col>

                        <v-col
                          cols="4"
                          class="text-center grey--text text--darken-1"
                        >
                          <span class="font-weight-bold"
                            >{{ progressPercent }}%</span
                          >
                        </v-col>

                        <v-col cols="4" class="text-right">
                          <span class="font-weight-bold">{{
                            Campaigns.visitorCount || 0
                          }}</span>
                          <span class="grey--text text--darken-1">
                            {{ visitorsWord }}</span
                          >
                        </v-col>
                      </v-row>
                    </v-col>
                  </v-row>
                </div>

                <!-- FAST style -->
                <div v-else>
                  <v-row dense>
                    <v-col cols="6" class="text-left">
                      <div class="stat-label">Raised</div>
                      <div class="stat-value">
                        {{ raisedAmount | currency }}
                      </div>
                    </v-col>

                    <v-col cols="6" class="text-right">
                      <div class="stat-label">Goal</div>
                      <div class="stat-value">{{ goalAmount | currency }}</div>
                    </v-col>

                    <v-col cols="12" class="pt-1">
                      <v-progress-linear
                        :value="progressPercent"
                        height="10"
                        rounded
                      />

                      <!-- <div class="text-body-2 grey--text text--darken-1 mt-2">
                        Days: <strong>{{ daysTotal }}</strong
                        >, Sponsored: <strong>{{ daysSponsored }}</strong
                        >, Left: <strong>{{ daysLeft }}</strong>
                      </div> -->

                      <div class="text-body-2 grey--text text--darken-1 mt-2">
                        Total: <strong>{{ daysTotal }}</strong
                        >, Sponsored: <strong>{{ daysSponsored }}</strong
                        >, Left: <strong>{{ daysLeft }}</strong>
                      </div>

                      <v-row dense no-gutters class="mt-2 meta-row">
                        <v-col cols="4" class="text-left">
                          <span class="font-weight-bold">{{
                            Campaigns.donationCount || 0
                          }}</span>
                          <span class="grey--text text--darken-1">
                            donations</span
                          >
                        </v-col>

                        <v-col
                          cols="4"
                          class="text-center grey--text text--darken-1"
                        >
                          <span class="font-weight-bold"
                            >{{ progressPercent }}%</span
                          >
                        </v-col>

                        <v-col cols="4" class="text-right">
                          <span class="font-weight-bold">{{
                            Campaigns.visitorCount || 0
                          }}</span>
                          <span class="grey--text text--darken-1">
                            {{ visitorsWord }}</span
                          >
                        </v-col>
                      </v-row>
                    </v-col>
                  </v-row>
                </div>
              </div>

              <v-divider></v-divider>

              <div class="my-4">
                <SponsorSeatGrid
                  v-if="
                    isInventoryCampaign && !isFastCampaign && showDetailGrid
                  "
                  :details="detailRows"
                  :kind="detailKind"
                  @donate="donationNow"
                />
              </div>

              <!-- Description -->
              <div
                v-if="cleanDescription"
                class="campaign-desc"
                v-html="cleanDescription"
              ></div>

              <div v-else class="text-body-2 grey--text text--darken-1">
                No description provided.
              </div>
            </v-card-text>
          </v-card>

          <!-- Desktop helper card -->
          <v-card class="card-soft d-none d-md-block mb-4" outlined>
            <v-card-text class="pa-4">
              <v-row align="center">
                <v-col cols="8">
                  <div class="text-subtitle-1 font-weight-bold">
                    Support this campaign
                  </div>
                  <div class="text-body-2 grey--text text--darken-1 mt-1">
                    Every donation helps, you're almost there.
                  </div>
                </v-col>
                <v-col cols="4" class="text-right">
                  <v-btn color="success" large rounded @click="donationNow">
                    Donate Now
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Sticky Donation Bar -->
          <div class="sticky-donate">
            <div class="sticky-inner">
              <div class="sticky-left">
                <div class="sticky-amount">
                  <span class="label">Raised</span>
                  <span class="value">{{ raisedAmount | currency }}</span>
                </div>

                <div class="sticky-meta">
                  <span class="chip">
                    <span class="num">{{ Campaigns.donationCount || 0 }}</span>
                    <span class="txt">donations</span>
                  </span>

                  <span class="sep">|</span>

                  <span class="chip">
                    <span class="num">{{ Campaigns.visitorCount || 0 }}</span>
                    <span class="txt">{{ visitorsWord }}</span>
                  </span>

                  <span class="sep">|</span>

                  <span class="chip">
                    <span class="num">{{ progressPercent }}%</span>
                    <span class="txt">to goal</span>
                  </span>
                </div>

                <v-progress-linear
                  :value="progressPercent"
                  height="8"
                  rounded
                  class="mt-2"
                />
              </div>

              <div class="sticky-right">
                <v-btn
                  color="success"
                  large
                  rounded
                  class="donate-btn"
                  @click="donationNow"
                >
                  Donate Now
                </v-btn>
              </div>
            </div>
          </div>

          <div class="text-center mt-4 footer-note">
            Powered by <span class="font-weight-bold">taleem.app</span>
          </div>
        </div>

        <!-- EXPIRED -->
        <div v-else-if="Campaigns && !Campaigns.isActive">
          <v-card class="card-soft" outlined>
            <v-card-text class="pa-5">
              <v-row align="center" justify="center" no-gutters>
                <v-col cols="12" class="text-center">
                  <lottie
                    class="mb-3"
                    :options="defaultOptions"
                    v-on:animCreated="handleAnimation"
                  />
                  <div class="text-h5 font-weight-bold">
                    This Link has Expired
                  </div>
                  <div class="text-body-2 grey--text text--darken-1 mt-2">
                    Please contact customer support to receive a new URL
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </div>

        <!-- LOADING -->
        <div v-else class="py-10 text-center">
          <v-progress-circular indeterminate size="34" />
          <div class="text-body-2 grey--text text--darken-1 mt-3">
            Loading campaign...
          </div>
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import campaignService from "@/modules/Campaign/service.js";
import Lottie from "@/components/lottie.vue";
import SponsorSeatGrid from "@/modules/Campaign/view/Donation/SponsorSeatGrid.vue";
import * as animationData from "@/assets/lottie/pinjump.json";

export default {
  name: "collectDonationByCampaign",
  data() {
    return {
      Campaigns: null,
      Business: null,
      defaultOptions: {
        animationData: animationData.default,
      },
    };
  },
  components: { Lottie, SponsorSeatGrid },
  created() {
    console.log("loading campaign");
    this.loadCampaign();
  },
  computed: {
    isFastCampaign() {
      const style = (this.Campaigns?.metadata?.detailStyle || "")
        .toString()
        .trim()
        .toLowerCase();
      return style === "fast";
    },
    detailKind() {
      const c = this.Campaigns || {};
      const md = c.metadata || {};
      const style = md.detailStyle || "normal";

      if (style === "units") return "unit";
      if (style === "fast") return "fast";
      if (style === "student") return "student";

      return md.detailKind || c.detailKind || "normal";
    },
    showDetailGrid() {
      return this.detailKind !== "normal" && this.isInventoryCampaign;
    },
    daysTotal() {
      return this.detailRows.length;
    },
    daysSponsored() {
      // sponsored if received >= amount
      return this.detailRows.filter(
        (d) => (Number(d.received) || 0) >= (Number(d.amount) || 0)
      ).length;
    },
    daysLeft() {
      return Math.max(0, this.daysTotal - this.daysSponsored);
    },
    isInventoryCampaign() {
      const c = this.Campaigns || {};
      const md = c.metadata || {};

      // If campaign is explicitly sponsor type
      const isSponsorType =
        c.type === "sponsor" ||
        c.campaignType === "sponsor" ||
        md.type === "sponsor" ||
        md.campaignType === "sponsor";

      // If campaign has details (new or legacy)
      const hasDetails =
        (Array.isArray(c.CampaignDetails) && c.CampaignDetails.length > 0) ||
        (Array.isArray(md.detail) && md.detail.length > 0);

      // If metadata explicitly says inventory style
      const inventoryFlag =
        md.detailStyle === "grid" ||
        md.detailStyle === "inventory" ||
        md.detailStyle === "fast" ||
        md.detailStyle === "student" ||
        md.detailStyle === "units" || // ✅ ADD THIS
        md.donationLimit === true ||
        md.donationLimit === "true" ||
        md.hasInventory === true;

      // Show grid for sponsor OR any campaign that actually has details/inventory
      return isSponsorType || inventoryFlag || hasDetails;
    },
    detailRows() {
      if (
        Array.isArray(this.Campaigns?.CampaignDetails) &&
        this.Campaigns.CampaignDetails.length
      ) {
        return this.Campaigns.CampaignDetails;
      }
      return this.Campaigns?.metadata?.detail || [];
    },
    fastTotal() {
      return this.detailRows.length;
    },
    fastSponsored() {
      return this.detailRows.filter((d) => this.isDayTaken(d)).length;
    },
    fastLeft() {
      return Math.max(0, this.fastTotal - this.fastSponsored);
    },
    cleanDescription() {
      const html =
        this.Campaigns && this.Campaigns.description
          ? String(this.Campaigns.description)
          : "";
      if (!html) return "";

      return (
        html
          // remove empty paragraphs like: <p class="...">&nbsp;</p> or <p><br></p>
          .replace(/<p[^>]*>(?:\s|&nbsp;|<br\s*\/?>)*<\/p>/gi, "")
          // trim extra whitespace
          .trim()
      );
    },
    visitorsWord() {
      const count =
        this.Campaigns && this.Campaigns.visitorCount != null
          ? Number(this.Campaigns.visitorCount)
          : 0;

      return count === 1 ? "visitor" : "visitors";
    },
    visitorLabel() {
      const count =
        this.Campaigns && this.Campaigns.visitorCount
          ? Number(this.Campaigns.visitorCount)
          : 0;
      return count <= 1 ? " visitor" : " visitors";
    },
    hasAddress() {
      return (
        this.Business &&
        (this.Business.address ||
          this.Business.city ||
          this.Business.state ||
          this.Business.zip)
      );
    },
    goalAmount() {
      const c = this.Campaigns || {};

      // if (this.isInventoryCampaign) {
      //   return this.detailRows.reduce((sum, d) => {
      //     const amount = Number(d.amount) || 0;
      //     const cap =
      //       d.capacity === null || d.capacity === undefined || d.capacity === ""
      //         ? 1
      //         : Number(d.capacity) || 0;
      //     return sum + amount * cap;
      //   }, 0);
      // }
      if (this.isInventoryCampaign) {
        return this.detailRows.reduce((sum, d) => {
          const amount = Number(d.amount) || 0;

          const capRaw = d.capacity ?? d.totalQuantity ?? d.metadata?.capacity;

          const cap =
            capRaw === null ||
            capRaw === undefined ||
            capRaw === "" ||
            Number(capRaw) <= 0
              ? 1
              : Number(capRaw) || 1;

          return sum + amount * cap;
        }, 0);
      }

      // normal fallback
      let raw = 0;
      if (c.donationGoal !== undefined && c.donationGoal !== null)
        raw = c.donationGoal;
      else if (c.dontationGoal !== undefined && c.dontationGoal !== null)
        raw = c.dontationGoal;
      else if (c.goal !== undefined && c.goal !== null) raw = c.goal;

      return Number(raw) || 0;
    },

    raisedAmount() {
      const c = this.Campaigns || {};

      if (this.isInventoryCampaign) {
        return this.detailRows.reduce(
          (sum, d) => sum + (Number(d.received) || 0),
          0
        );
      }

      // normal fallback
      let raw = null;
      if (c.raisedAmount !== undefined && c.raisedAmount !== null)
        raw = c.raisedAmount;
      else if (c.raised !== undefined && c.raised !== null) raw = c.raised;
      else if (c.raisedTotal !== undefined && c.raisedTotal !== null)
        raw = c.raisedTotal;

      if (raw === null) return this.goalAmount;
      return Number(raw) || 0;
    },

    progressPercent() {
      const goal = this.goalAmount;
      const raised = this.raisedAmount;

      if (!goal || goal <= 0) return 0;

      const pct = Math.floor((raised / goal) * 100);
      if (pct < 0) return 0;
      if (pct > 100) return 100;
      return pct;
    },
  },
  methods: {
    isDayTaken(d) {
      // common possibilities across versions
      if (!d) return false;

      // explicit flags
      if (d.sponsored === true) return true;
      if (d.isSponsored === true) return true;
      if (d.available === false) return true;
      if (d.isAvailable === false) return true;

      // capacity / inventory style
      if (Number(d.remaining) === 0) return true;
      if (Number(d.remainingUnits) === 0) return true;

      // money collected vs target amount
      const goal = Number(d.amount ?? d.goalAmount ?? d.price ?? 0);
      const raised = Number(
        d.raisedAmount ?? d.raised ?? d.collectedAmount ?? 0
      );
      if (goal > 0 && raised >= goal) return true;

      return false;
    },
    handleAnimation(anim) {
      this.anim = anim;
    },
    donationNow() {
      this.$router.push({ path: "/go/" + this.Campaigns.code + "/donate" });
    },
    loadCampaign() {
      const code = this.$route.params.id;

      campaignService
        .getCampaignById(code)
        .then((response) => {
          if (!response.data) {
            this.$router.push({ name: "pagenotfound" });
            return;
          }
          if (response.data.isActive === false) {
            this.$router.push({ name: "pagenotfound" });
            return;
          }
          console.log("response.data", response.data);
          this.Campaigns = response.data;
          this.Business = response.data.Business;
        })
        .catch((error) => {
          console.log("error getting campaign", error);
          this.$router.push({ name: "pagenotfound" });
        });
    },
  },
};
</script>

<style scoped>
.page-wrap {
  padding: 16px;
  padding-bottom: 120px;
}

.card-soft {
  border-radius: 18px;
  overflow: hidden;
}

.hero-wrap {
  overflow: hidden;
}

.hero-img {
  border-top-left-radius: 18px;
  border-top-right-radius: 18px;
}

.stat-label {
  font-size: 12px;
  opacity: 0.75;
}
.stat-value {
  font-size: 20px;
  font-weight: 700;
  line-height: 1.2;
}
.meta-row {
  font-size: 13px;
}

.campaign-desc {
  line-height: 1.55;
  font-size: 14px;
}

/* Sticky Donate */
.sticky-donate {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 30;
  padding: 10px 12px;
}

.sticky-inner {
  max-width: 760px;
  margin: 0 auto;
  background: #fff;
  border-radius: 18px;
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.18);
  padding: 12px;
  display: flex;
  gap: 12px;
  align-items: center;
}

.sticky-left {
  flex: 1;
  min-width: 0;
}

.sticky-amount .label {
  font-size: 12px;
  opacity: 0.75;
  margin-right: 6px;
}
.sticky-amount .value {
  font-size: 16px;
  font-weight: 800;
}

.sticky-meta {
  margin-top: 4px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  opacity: 0.9;
}

.sep {
  opacity: 0.6;
}

.chip .num {
  font-weight: 800;
  margin-right: 4px;
}

.donate-btn {
  white-space: nowrap;
}

.footer-note {
  opacity: 0.85;
}
/* Quill HTML normalization */
.campaign-desc {
  line-height: 1.55;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.78);
  word-break: break-word;
}

/* Vuetify scoped styles need :deep() to reach v-html content */
.campaign-desc :deep(p) {
  margin: 0 0 10px;
}

.campaign-desc :deep(p:last-child) {
  margin-bottom: 0;
}

.campaign-desc :deep(strong) {
  font-weight: 800;
}
.meta-row {
  font-size: 13px;
  line-height: 1.2;
}
.campaign-desc :deep(ul),
.campaign-desc :deep(ol) {
  margin: 0 0 10px;
  padding-left: 18px;
}

/* Quill alignment classes */
.campaign-desc :deep(.ql-align-center) {
  text-align: center;
}

.campaign-desc :deep(.ql-align-right) {
  text-align: right;
}

.campaign-desc :deep(.ql-align-left) {
  text-align: left;
}

.day-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 10px;
}

.day-tile {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 14px;
  padding: 10px 8px;
  text-align: center;
  cursor: pointer;
}

.day-tile.done {
  background: rgba(0, 0, 0, 0.06);
  opacity: 0.75;
}

.day-num {
  font-weight: 800;
  font-size: 14px;
}
.day-status {
  font-size: 11px;
  opacity: 0.8;
  margin-top: 2px;
}
</style>